import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import laboratoryInfo from '../config';
import { getLotNumbers, getApplicableStandardsText } from '../data/materialLotData';
import { FaPrint, FaDownload, FaTimes, FaSpinner, FaTruck, FaFileAlt, FaUserInjured, FaFileInvoiceDollar, FaCalculator } from 'react-icons/fa';

/**
 * BulkDocumentModal
 * Modal universel pour la génération, impression 1-clic et téléchargement en masse
 * de tout type de document (BL, Fiches de Travail, Fiches Patient, Devis, Factures)
 */
const BulkDocumentModal = ({ orders = [], products = [], clients = [], docType = 'BL', onClose }) => {
    const [activeDocType, setActiveDocType] = useState(docType);
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: orders.length });
    const printAreaRef = useRef(null);

    const getDocTitle = () => {
        if (activeDocType === 'WORKSHEET') return 'Fiches de Travail';
        if (activeDocType === 'PATIENT') return 'Fiches Patient';
        if (activeDocType === 'QUOTE') return 'Devis';
        if (activeDocType === 'INVOICE') return 'Factures';
        return 'Bons de Livraison (BL)';
    };

    const getDocPrefix = () => {
        if (activeDocType === 'WORKSHEET') return 'Fiche-Travail';
        if (activeDocType === 'PATIENT') return 'Fiche-Patient';
        if (activeDocType === 'QUOTE') return 'Devis';
        if (activeDocType === 'INVOICE') return 'Facture';
        return 'BL';
    };

    // Extraction des items et calcul du total d'un cas
    const getOrderDetails = (order) => {
        const items = [];
        let total = 0;

        if (order && order.teethConfig) {
            if (Array.isArray(order.teethConfig)) {
                order.teethConfig.forEach(line => {
                    const { productId, teeth, details, notes } = line;
                    const product = products.find(p => p.id === productId);

                    if (product) {
                        let priceKey = 'priceClassique';
                        if (order.gamme === 'paris') priceKey = 'priceParis';
                        if (order.gamme === 'eco') priceKey = 'priceEco';

                        const unitPrice = parseFloat(product[priceKey] ?? product.priceClassique ?? 0);
                        const quantity = line.quantity !== undefined && line.quantity !== null && !isNaN(Number(line.quantity)) 
                            ? Number(line.quantity) 
                            : (teeth.length || 1);
                        const isWarranty = Number(quantity) === 0 || Boolean(order.notes && order.notes.includes('[REDO'));
                        const itemPrice = isWarranty ? 0 : unitPrice * quantity;
                        const toothLabel = teeth.map(t => t === 'HAUT' ? 'Haut' : t === 'BAS' ? 'Bas' : t).join(', ');
                        let description = `${product.name}`;

                        if (details && details.teinte) {
                            description += `, Teinte ${details.teinte}`;
                        }

                        items.push({ toothId: toothLabel, description, name: product.name, quantity: isWarranty ? 0 : quantity, unitPrice, price: itemPrice, category: product.category, details, notes });
                        total += itemPrice;
                    }
                });
            } else {
                Object.entries(order.teethConfig).forEach(([toothId, config]) => {
                    const { productId, details, notes } = config;
                    const product = products.find(p => p.id === productId);

                    if (product) {
                        let priceKey = 'priceClassique';
                        if (order.gamme === 'paris') priceKey = 'priceParis';
                        if (order.gamme === 'eco') priceKey = 'priceEco';

                        const unitPrice = parseFloat(product[priceKey] ?? product.priceClassique ?? 0);
                        const isWarranty = Boolean(order.notes && order.notes.includes('[REDO'));
                        const itemPrice = isWarranty ? 0 : unitPrice;
                        let description = `${product.name}`;

                        if (details && details.teinte) {
                            description += `, Teinte ${details.teinte}`;
                        }

                        items.push({ toothId, description, name: product.name, quantity: isWarranty ? 0 : 1, unitPrice, price: itemPrice, category: product.category, details, notes });
                        total += itemPrice;
                    }
                });
            }
        }
        return { items, total };
    };

    const handlePrintAll = () => {
        const printContent = document.getElementById('bulk-print-area');
        if (!printContent) {
            window.print();
            return;
        }

        // Supprimer tout ancien iframe d'impression
        const existingIframe = document.getElementById('bulk-print-iframe');
        if (existingIframe) {
            existingIframe.remove();
        }

        // Créer un iframe isolé dédié à l'impression
        const iframe = document.createElement('iframe');
        iframe.id = 'bulk-print-iframe';
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.visibility = 'hidden';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Impression - ${getDocTitle()} - ENOVADENT</title>
                <style>
                    @page {
                        size: A4 portrait;
                        margin: 0;
                    }
                    * {
                        box-sizing: border-box;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    html, body {
                        margin: 0;
                        padding: 0;
                        background: white;
                        font-family: Arial, sans-serif;
                        width: 100%;
                    }
                    .bulk-print-page {
                        width: 210mm !important;
                        min-height: 297mm !important;
                        height: auto !important;
                        padding: 20mm !important;
                        margin: 0 auto !important;
                        background: white !important;
                        page-break-after: always !important;
                        break-after: page !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                        box-sizing: border-box !important;
                        box-shadow: none !important;
                    }
                    .bulk-print-page:last-child {
                        page-break-after: auto !important;
                        break-after: auto !important;
                    }
                    table {
                        page-break-inside: avoid;
                    }
                </style>
            </head>
            <body>
                ${printContent.innerHTML}
            </body>
            </html>
        `);
        doc.close();

        // Attendre que les polices et images chargent puis lancer l'impression
        setTimeout(() => {
            try {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
            } catch (err) {
                console.error("Erreur impression iframe:", err);
                window.print();
            }
            // Nettoyage après impression
            setTimeout(() => {
                if (iframe && iframe.parentNode) {
                    iframe.remove();
                }
            }, 2000);
        }, 300);
    };

    // Téléchargement en masse (images JPG) — capture parallèle + téléchargement rapide
    const handleDownloadAll = async () => {
        if (isGenerating) return;
        setIsGenerating(true);
        setProgress({ current: 0, total: orders.length });

        const prefix = getDocPrefix();

        // Small delay to let DOM stabilize after possible tab switch
        await new Promise(res => setTimeout(res, 200));

        // Phase 1: Pre-capture all canvases in parallel batches of 3
        const captures = [];
        const BATCH_SIZE = 3;

        for (let batchStart = 0; batchStart < orders.length; batchStart += BATCH_SIZE) {
            const batchEnd = Math.min(batchStart + BATCH_SIZE, orders.length);
            const batchPromises = [];

            for (let i = batchStart; i < batchEnd; i++) {
                const order = orders[i];
                const elementId = `bulk-doc-page-${activeDocType}-${order.id || i}`;
                const element = document.getElementById(elementId);

                if (element) {
                    batchPromises.push(
                        html2canvas(element, {
                            scale: 2,
                            useCORS: true,
                            backgroundColor: '#ffffff',
                            logging: false,
                            windowWidth: 1200
                        }).then(canvas => {
                            return new Promise(resolve => {
                                canvas.toBlob(blob => {
                                    const caseNum = order.caseNumber || (typeof order.id === 'string' ? order.id.substring(0, 8).toUpperCase() : `CAS-${i + 1}`);
                                    const cleanPatient = (order.patientName || 'patient').replace(/[^a-zA-Z0-9_\-]/g, '_');
                                    resolve({ blob, fileName: `${prefix}-${caseNum}-${cleanPatient}.jpg`, index: i });
                                }, 'image/jpeg', 0.92);
                            });
                        }).catch(err => {
                            console.error(`Erreur capture ${order.id}:`, err);
                            return null;
                        })
                    );
                }
            }

            const batchResults = await Promise.all(batchPromises);
            captures.push(...batchResults.filter(Boolean));
            setProgress({ current: Math.min(batchEnd, orders.length), total: orders.length });
        }

        // Phase 2: Trigger downloads rapidly with minimal delay
        for (let i = 0; i < captures.length; i++) {
            const { blob, fileName } = captures[i];
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();

            // Clean up
            setTimeout(() => {
                URL.revokeObjectURL(blobUrl);
                if (link.parentNode) document.body.removeChild(link);
            }, 300);

            // Short delay between downloads to avoid browser blocking
            if (i < captures.length - 1) {
                await new Promise(res => setTimeout(res, 400));
            }
        }

        setIsGenerating(false);
    };

    const handleTabChange = (newType) => {
        if (isGenerating) return;
        setActiveDocType(newType);
    };

    return (
        <div className="bulk-modal-backdrop" style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.85)', zIndex: 9999,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(6px)', padding: '15px'
        }}>
            <style>{`
                @media print {
                    html, body {
                        width: 100% !important;
                        height: auto !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: white !important;
                        overflow: visible !important;
                    }
                    body * {
                        visibility: hidden !important;
                    }
                    .bulk-modal-backdrop,
                    .bulk-modal-container,
                    .bulk-scroll-area {
                        position: static !important;
                        display: block !important;
                        width: 100% !important;
                        height: auto !important;
                        min-height: auto !important;
                        max-height: none !important;
                        overflow: visible !important;
                        box-shadow: none !important;
                        border: none !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        background: white !important;
                    }
                    .bulk-modal-header,
                    .bulk-progress-bar {
                        display: none !important;
                    }
                    #bulk-print-area, #bulk-print-area * {
                        visibility: visible !important;
                    }
                    #bulk-print-area {
                        position: static !important;
                        display: block !important;
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: white !important;
                        overflow: visible !important;
                    }
                    .bulk-print-page {
                        position: relative !important;
                        display: block !important;
                        width: 210mm !important;
                        min-height: 297mm !important;
                        height: auto !important;
                        margin: 0 auto !important;
                        padding: 20mm !important;
                        page-break-after: always !important;
                        break-after: page !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                        box-shadow: none !important;
                        border: none !important;
                        background: white !important;
                        box-sizing: border-box !important;
                    }
                    .bulk-print-page:last-child {
                        page-break-after: auto !important;
                        break-after: auto !important;
                    }
                    @page {
                        size: A4 portrait;
                        margin: 0;
                    }
                }
            `}</style>

            <div className="bulk-modal-container" style={{
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '980px',
                height: '93vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
                {/* Header Toolbar */}
                <div className="bulk-modal-header" style={{
                    display: 'flex',
                    justify: 'space-between',
                    alignItems: 'center',
                    padding: '14px 24px',
                    backgroundColor: '#1e293b',
                    color: 'white',
                    flexWrap: 'wrap',
                    gap: '12px'
                }}>
                    <div className="d-flex align-items-center gap-3">
                        <div style={{
                            width: '40px', height: '40px', borderRadius: '50%',
                            backgroundColor: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            {activeDocType === 'WORKSHEET' && <FaFileAlt size={20} className="text-white" />}
                            {activeDocType === 'PATIENT' && <FaUserInjured size={20} className="text-white" />}
                            {activeDocType === 'QUOTE' && <FaCalculator size={20} className="text-white" />}
                            {activeDocType === 'INVOICE' && <FaFileInvoiceDollar size={20} className="text-white" />}
                            {activeDocType === 'BL' && <FaTruck size={20} className="text-white" />}
                        </div>
                        <div>
                            <h5 className="m-0 fw-bold">Génération en masse — {getDocTitle()}</h5>
                            <small style={{ color: '#94a3b8' }}>{orders.length} dossier{orders.length > 1 ? 's' : ''} sélectionné{orders.length > 1 ? 's' : ''}</small>
                        </div>
                    </div>

                    {/* Selector Tabs */}
                    <div className="btn-group shadow-sm bg-dark p-1 rounded-pill flex-wrap">
                        <button
                            className={`btn btn-sm rounded-pill px-3 fw-bold ${activeDocType === 'BL' ? 'btn-primary' : 'btn-dark text-white-50'}`}
                            onClick={() => handleTabChange('BL')}
                            disabled={isGenerating}
                            style={{ fontSize: '12px' }}
                        >
                            🚚 BL
                        </button>
                        <button
                            className={`btn btn-sm rounded-pill px-3 fw-bold ${activeDocType === 'WORKSHEET' ? 'btn-primary' : 'btn-dark text-white-50'}`}
                            onClick={() => handleTabChange('WORKSHEET')}
                            disabled={isGenerating}
                            style={{ fontSize: '12px' }}
                        >
                            📋 Fiche Travail
                        </button>
                        <button
                            className={`btn btn-sm rounded-pill px-3 fw-bold ${activeDocType === 'PATIENT' ? 'btn-primary' : 'btn-dark text-white-50'}`}
                            onClick={() => handleTabChange('PATIENT')}
                            disabled={isGenerating}
                            style={{ fontSize: '12px' }}
                        >
                            👤 Fiche Patient
                        </button>
                        <button
                            className={`btn btn-sm rounded-pill px-3 fw-bold ${activeDocType === 'QUOTE' ? 'btn-primary' : 'btn-dark text-white-50'}`}
                            onClick={() => handleTabChange('QUOTE')}
                            disabled={isGenerating}
                            style={{ fontSize: '12px' }}
                        >
                            📄 Devis
                        </button>
                        <button
                            className={`btn btn-sm rounded-pill px-3 fw-bold ${activeDocType === 'INVOICE' ? 'btn-primary' : 'btn-dark text-white-50'}`}
                            onClick={() => handleTabChange('INVOICE')}
                            disabled={isGenerating}
                            style={{ fontSize: '12px' }}
                        >
                            💶 Facture
                        </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex align-items-center gap-2 ms-auto">
                        <button
                            onClick={handlePrintAll}
                            disabled={isGenerating}
                            className="btn btn-success px-3 fw-bold d-flex align-items-center gap-2 shadow-sm rounded-pill"
                            style={{ fontSize: '13px' }}
                        >
                            <FaPrint /> Imprimer tout ({orders.length})
                        </button>

                        <button
                            onClick={handleDownloadAll}
                            disabled={isGenerating}
                            className="btn btn-primary px-3 fw-bold d-flex align-items-center gap-2 shadow-sm rounded-pill"
                            style={{ fontSize: '13px' }}
                        >
                            {isGenerating ? <FaSpinner className="fa-spin" /> : <FaDownload />}
                            {isGenerating ? `${progress.current}/${progress.total}...` : `Télécharger JPG (${orders.length})`}
                        </button>

                        <button
                            onClick={onClose}
                            disabled={isGenerating}
                            className="btn btn-outline-light px-3 fw-bold rounded-pill ms-1"
                            style={{ fontSize: '13px' }}
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {isGenerating && (
                    <div className="bulk-progress-bar" style={{ backgroundColor: '#2563eb', color: 'white', padding: '10px 24px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span><FaSpinner className="fa-spin me-2" /> Génération des fichiers JPG en cours ({progress.current} sur {progress.total})...</span>
                        <span className="fw-bold">{Math.round((progress.current / progress.total) * 100)}%</span>
                    </div>
                )}

                {/* Document View Stream */}
                <div className="bulk-scroll-area" style={{ flex: 1, overflowY: 'auto', padding: '24px', backgroundColor: '#e2e8f0' }}>
                    <div id="bulk-print-area" ref={printAreaRef}>
                        {orders.map((order, index) => {
                            const { items, total } = getOrderDetails(order);
                            const currentId = order.id || `idx-${index}`;
                            const elementId = `bulk-doc-page-${activeDocType}-${order.id || index}`;
                            const caseNumStr = order.caseNumber || (typeof currentId === 'string' ? currentId.substring(0, 8).toUpperCase() : 'PREVIEW');
                            const practitionerClient = clients.find(c => c.id === (order.clientId || order.client_id));

                            // ================= 1. FICHE DE TRAVAIL =================
                            if (activeDocType === 'WORKSHEET') {
                                return (
                                    <div key={`ws-${activeDocType}-${order.id || index}`} id={elementId} className="bulk-print-page mb-4 shadow-sm mx-auto" style={{ backgroundColor: 'white', padding: '20mm', color: 'black', width: '210mm', minHeight: '297mm', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', borderRadius: '8px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', borderBottom: '3px solid #1e293b', paddingBottom: '15px' }}>
                                            <div>
                                                <h2 style={{ margin: '0 0 5px 0', color: '#1e293b', fontSize: '20px' }}>{laboratoryInfo.name}</h2>
                                                <p style={{ margin: '2px 0', fontSize: '12px' }}>{laboratoryInfo.address}</p>
                                                <p style={{ margin: '2px 0', fontSize: '12px' }}>Tel: {laboratoryInfo.phone}</p>
                                                <p style={{ margin: '2px 0', fontSize: '12px' }}>{laboratoryInfo.email}</p>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <h1 style={{ margin: '0 0 5px 0', color: '#1e293b', fontSize: '22px' }}>FICHE DE TRAVAIL</h1>
                                                <p style={{ margin: '2px 0', fontWeight: 'bold', fontSize: '13px' }}>N° DOSSIER: {caseNumStr}</p>
                                                <p style={{ margin: '2px 0', fontSize: '13px' }}>Date: {new Date().toLocaleDateString('fr-FR')}</p>
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px', backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                            <div>
                                                <h3 style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>Informations Client</h3>
                                                <p style={{ fontWeight: 'bold', margin: '3px 0', fontSize: '14px', color: '#1e293b' }}>Dr. {order.practitionerName || '—'}</p>
                                                {practitionerClient?.address && <p style={{ margin: '2px 0', fontSize: '12px', color: '#64748b' }}>{practitionerClient.address}</p>}
                                            </div>
                                            <div>
                                                <h3 style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#64748b', textTransform: 'uppercase' }}>Informations Patient</h3>
                                                <p style={{ fontWeight: 'bold', margin: '3px 0', fontSize: '14px', color: '#1e293b' }}>{order.patientName || '—'}</p>
                                                {order.patientReference && <p style={{ margin: '2px 0', fontSize: '12px' }}>Réf: {order.patientReference}</p>}
                                                <p style={{ margin: '3px 0', fontSize: '13px' }}><strong>Gamme:</strong> {order.gamme ? (order.gamme.toLowerCase() === 'nova' ? 'FINITION NOVA' : order.gamme.toUpperCase()) : 'FINITION NOVA'}</p>
                                            </div>
                                        </div>

                                        <h4 style={{ backgroundColor: '#1e293b', color: '#fff', padding: '8px 12px', margin: '15px 0 10px 0', borderRadius: '4px', fontSize: '14px' }}>DÉTAILS TECHNIQUES</h4>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '25px' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid #1e293b' }}>
                                                    <th style={{ textAlign: 'center', padding: '10px 4px', width: '130px', color: '#475569', fontSize: '12px' }}>DENT</th>
                                                    <th style={{ textAlign: 'left', padding: '10px', color: '#475569', fontSize: '12px' }}>TRAVAIL À RÉALISER</th>
                                                    <th style={{ textAlign: 'center', padding: '10px', width: '80px', color: '#475569', fontSize: '12px' }}>QTÉ</th>
                                                    <th style={{ textAlign: 'left', padding: '10px', color: '#475569', fontSize: '12px' }}>TEINTE / NOTES</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((item, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={{ textAlign: 'center', padding: '12px 4px', fontWeight: 'bold', fontSize: '13px', color: '#1e293b' }}>{item.toothId}</td>
                                                        <td style={{ padding: '12px' }}>
                                                            <div style={{ fontWeight: 'bold', color: '#334155', fontSize: '13px' }}>{item.category}</div>
                                                            <div style={{ fontSize: '13px', color: '#475569' }}>{item.name}</div>
                                                        </td>
                                                        <td style={{ textAlign: 'center', padding: '12px', fontSize: '13px', fontWeight: 'bold' }}>{item.quantity}</td>
                                                        <td style={{ padding: '12px' }}>
                                                            {item.details?.teinte && <div style={{ fontSize: '13px' }}><strong>Teinte:</strong> {item.details.teinte}</div>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px' }}>
                                            <div style={{ border: '1px solid #e2e8f0', padding: '12px', borderRadius: '8px', minHeight: '100px' }}>
                                                <h4 style={{ margin: '0 0 8px 0', borderBottom: '1px solid #f1f5f9', paddingBottom: '6px', fontSize: '13px', color: '#475569' }}>NOTES LABORATOIRE</h4>
                                                {items.filter(item => item.notes && item.notes.trim() !== '').map((item, idx) => (
                                                    <div key={idx} style={{ fontSize: '12px', marginBottom: '4px' }}>
                                                        <strong>Dent(s) {item.toothId} :</strong> {item.notes}
                                                    </div>
                                                ))}
                                            </div>
                                            <div style={{ border: '1px solid #cbd5e1', padding: '12px', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                                                <h4 style={{ margin: '0 0 8px 0', borderBottom: '1px solid #cbd5e1', paddingBottom: '6px', fontSize: '13px', color: '#1e3a8a' }}>TRAÇABILITÉ DES MATÉRIAUX & NORMES ISO / UE</h4>
                                                <div style={{ fontSize: '10.5px', color: '#334155', lineHeight: '1.8' }}>
                                                    {(() => {
                                                        const detectedLots = [];
                                                        const seenLotLabels = new Set();
                                                        items.forEach(item => {
                                                            const lots = getLotNumbers(item.category, item.name, item.details?.teinte, item.notes);
                                                            lots.forEach(l => {
                                                                if (!seenLotLabels.has(l.label)) {
                                                                    seenLotLabels.add(l.label);
                                                                    detectedLots.push(l);
                                                                }
                                                            });
                                                        });
                                                        if (detectedLots.length > 0) {
                                                            return detectedLots.map((lot, idx) => (
                                                                <div key={idx}><strong>{lot.label} :</strong> {lot.value}</div>
                                                            ));
                                                        }
                                                        return 'Matériaux : Lot ____________________';
                                                    })()}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Déclaration d'engagement et de conformité DM sur mesure */}
                                        <div style={{ marginBottom: '20px', border: '1px solid #cbd5e1', padding: '10px 14px', borderRadius: '6px', backgroundColor: '#ffffff' }}>
                                            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#1e293b', marginBottom: '3px', textTransform: 'uppercase' }}>
                                                Déclaration de Conformité — Dispositif Médical sur Mesure (DMSM)
                                            </div>
                                            <p style={{ margin: '0', fontSize: '10px', color: '#475569', lineHeight: '1.4', textAlign: 'justify' }}>
                                                {(() => {
                                                    const detectedLots = [];
                                                    const seenLotLabels = new Set();
                                                    items.forEach(item => {
                                                        const lots = getLotNumbers(item.category, item.name, item.details?.teinte, item.notes);
                                                        lots.forEach(l => {
                                                            if (!seenLotLabels.has(l.label)) {
                                                                seenLotLabels.add(l.label);
                                                                detectedLots.push(l);
                                                            }
                                                        });
                                                    });
                                                    return `Le laboratoire ${laboratoryInfo.name} certifie que ce dispositif médical sur mesure a été fabriqué exclusivement pour le patient susnommé conformément à la prescription médicale du praticien et répond aux exigences générales de sécurité et de performances du Règlement Européen (UE) 2017/745 (MDR) et ${getApplicableStandardsText(detectedLots)}`;
                                                })()}
                                            </p>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '25px', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
                                            <div style={{ textAlign: 'center', border: '1px solid #94a3b8', borderRadius: '8px', padding: '6px 14px', fontSize: '11px', color: '#475569', minWidth: '150px' }}>
                                                <strong>Contrôle Qualité de Libération</strong>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '35px', marginTop: '4px' }}>
                                                    <img src="/signature.png" alt="Signature" style={{ height: '45px', maxHeight: '45px', objectFit: 'contain' }} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            // ================= 2. FICHE PATIENT =================
                            if (activeDocType === 'PATIENT') {
                                return (
                                    <div key={`pt-${activeDocType}-${order.id || index}`} id={elementId} className="bulk-print-page mb-4 shadow-sm mx-auto" style={{ backgroundColor: 'white', padding: '20mm', color: 'black', width: '210mm', minHeight: '297mm', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', borderRadius: '8px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', borderBottom: '3px solid #166534', paddingBottom: '15px' }}>
                                            <div>
                                                <h2 style={{ margin: '0 0 5px 0', color: '#166534', fontSize: '20px' }}>{laboratoryInfo.name}</h2>
                                                <p style={{ margin: '2px 0', fontSize: '12px' }}>{laboratoryInfo.address}</p>
                                                <p style={{ margin: '2px 0', fontSize: '12px' }}>Tel: {laboratoryInfo.phone}</p>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <h1 style={{ margin: '0 0 5px 0', color: '#166534', fontSize: '22px' }}>FICHE PATIENT</h1>
                                                <p style={{ margin: '2px 0', fontWeight: 'bold', fontSize: '13px' }}>N° DOSSIER: {caseNumStr}</p>
                                                <p style={{ margin: '2px 0', fontSize: '13px' }}>Date: {new Date().toLocaleDateString('fr-FR')}</p>
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px', backgroundColor: '#f0fdf4', padding: '15px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                                            <div>
                                                <h3 style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#166534', textTransform: 'uppercase' }}>Patient</h3>
                                                <p style={{ fontWeight: 'bold', margin: '3px 0', fontSize: '15px', color: '#14532d' }}>{order.patientName || '—'}</p>
                                                {order.patientReference && <p style={{ margin: '2px 0', fontSize: '12px' }}>Réf: {order.patientReference}</p>}
                                            </div>
                                            <div>
                                                <h3 style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#166534', textTransform: 'uppercase' }}>Praticien</h3>
                                                <p style={{ fontWeight: 'bold', margin: '3px 0', fontSize: '15px', color: '#14532d' }}>Dr. {order.practitionerName || '—'}</p>
                                                {practitionerClient?.address && <p style={{ margin: '2px 0', fontSize: '12px', color: '#475569' }}>{practitionerClient.address}</p>}
                                            </div>
                                        </div>

                                        <h4 style={{ backgroundColor: '#166534', color: '#fff', padding: '8px 12px', margin: '15px 0 10px 0', borderRadius: '4px', fontSize: '14px' }}>DISPOSITIF MÉDICAL SUR MESURE</h4>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '25px' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid #166534' }}>
                                                    <th style={{ textAlign: 'center', padding: '10px 4px', width: '130px', color: '#166534', fontSize: '12px' }}>DENT</th>
                                                    <th style={{ textAlign: 'left', padding: '10px', color: '#166534', fontSize: '12px' }}>DÉSIGNATION</th>
                                                    <th style={{ textAlign: 'center', padding: '10px', width: '80px', color: '#166534', fontSize: '12px' }}>QTÉ</th>
                                                    <th style={{ textAlign: 'left', padding: '10px', color: '#166534', fontSize: '12px' }}>TEINTE</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((item, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={{ textAlign: 'center', padding: '12px 4px', fontWeight: 'bold', fontSize: '13px', color: '#14532d' }}>{item.toothId}</td>
                                                        <td style={{ padding: '12px' }}>
                                                            <div style={{ fontWeight: 'bold', color: '#14532d', fontSize: '13px' }}>{item.category}</div>
                                                            <div style={{ fontSize: '13px', color: '#334155' }}>{item.name}</div>
                                                        </td>
                                                        <td style={{ textAlign: 'center', padding: '12px', fontSize: '13px', fontWeight: 'bold' }}>{item.quantity}</td>
                                                        <td style={{ padding: '12px' }}>
                                                            {item.details?.teinte && <div style={{ fontSize: '13px' }}><strong>Teinte:</strong> {item.details.teinte}</div>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Notes & Remarques Section */}
                                        {order.notes && order.notes.trim() !== '' && (
                                            <div style={{ marginBottom: '20px', border: '1px solid #bbf7d0', padding: '12px', borderRadius: '8px', backgroundColor: '#f0fdf4' }}>
                                                <h4 style={{ margin: '0 0 8px 0', borderBottom: '1px solid #bbf7d0', paddingBottom: '6px', fontSize: '13px', color: '#166534' }}>NOTES & REMARQUES</h4>
                                                <div style={{ fontSize: '12px', color: '#14532d', backgroundColor: '#ffffff', padding: '8px', borderRadius: '4px', borderLeft: '3px solid #22c55e', whiteSpace: 'pre-wrap' }}>
                                                    <strong>Remarque générale :</strong> {order.notes}
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ border: '1px solid #bbf7d0', padding: '12px', borderRadius: '8px', backgroundColor: '#f0fdf4', marginBottom: '20px' }}>
                                            <h4 style={{ margin: '0 0 8px 0', borderBottom: '1px solid #bbf7d0', paddingBottom: '6px', fontSize: '13px', color: '#166534' }}>TRAÇABILITÉ DES MATÉRIAUX & NORMES ISO / UE</h4>
                                            <div style={{ fontSize: '10.5px', color: '#334155', lineHeight: '1.8' }}>
                                                {(() => {
                                                    const detectedLots = [];
                                                    const seenLotLabels = new Set();
                                                    items.forEach(item => {
                                                        const lots = getLotNumbers(item.category, item.name, item.details?.teinte, item.notes);
                                                        lots.forEach(l => {
                                                            if (!seenLotLabels.has(l.label)) {
                                                                seenLotLabels.add(l.label);
                                                                detectedLots.push(l);
                                                            }
                                                        });
                                                    });
                                                    if (detectedLots.length > 0) {
                                                        return detectedLots.map((lot, idx) => (
                                                            <div key={idx}><strong>{lot.label} :</strong> {lot.value}</div>
                                                        ));
                                                    }
                                                    return 'Matériaux : Lot ____________________';
                                                })()}
                                            </div>
                                        </div>

                                        {/* Déclaration d'engagement et de conformité DM sur mesure */}
                                        <div style={{ marginBottom: '20px', border: '1.5px solid #86efac', padding: '10px 14px', borderRadius: '8px', backgroundColor: '#ffffff' }}>
                                            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#166534', marginBottom: '3px', textTransform: 'uppercase' }}>
                                                ⚖️ DÉCLARATION DE CONFORMITÉ — DISPOSITIF MÉDICAL SUR MESURE (DMSM)
                                            </div>
                                            <p style={{ margin: '0', fontSize: '10px', color: '#334155', lineHeight: '1.4', textAlign: 'justify' }}>
                                                {(() => {
                                                    const detectedLots = [];
                                                    const seenLotLabels = new Set();
                                                    items.forEach(item => {
                                                        const lots = getLotNumbers(item.category, item.name, item.details?.teinte, item.notes);
                                                        lots.forEach(l => {
                                                            if (!seenLotLabels.has(l.label)) {
                                                                seenLotLabels.add(l.label);
                                                                detectedLots.push(l);
                                                            }
                                                        });
                                                    });
                                                    return `Le laboratoire ${laboratoryInfo.name} certifie sur l’honneur que ce dispositif médical sur mesure a été conçu et fabriqué exclusivement pour le patient susnommé, conformément à la prescription médicale du praticien et répond aux exigences générales de sécurité et de performances de l’annexe I et XIII du Règlement Européen (UE) 2017/745 (MDR) et ${getApplicableStandardsText(detectedLots)}`;
                                                })()}
                                            </p>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '25px', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
                                            <div style={{ textAlign: 'center', border: '1px solid #166534', borderRadius: '8px', padding: '6px 14px', fontSize: '11px', color: '#166534', minWidth: '150px' }}>
                                                <strong>Contrôle Qualité de Libération</strong>
                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '35px', marginTop: '4px' }}>
                                                    <img src="/signature.png" alt="Signature" style={{ height: '45px', maxHeight: '45px', objectFit: 'contain' }} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            // ================= 3. DEVIS =================
                            if (activeDocType === 'QUOTE') {
                                return (
                                    <div key={`qt-${activeDocType}-${order.id || index}`} id={elementId} className="bulk-print-page mb-4 shadow-sm mx-auto" style={{ backgroundColor: 'white', padding: '20mm', color: 'black', width: '210mm', minHeight: '297mm', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', borderRadius: '8px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', borderBottom: '2px solid #1e40af', paddingBottom: '18px' }}>
                                            <div>
                                                <h2 style={{ margin: '0 0 8px 0', color: '#1e40af', fontSize: '20px' }}>{laboratoryInfo.name}</h2>
                                                <p style={{ margin: '2px 0', fontSize: '12px' }}>{laboratoryInfo.address}</p>
                                                <p style={{ margin: '2px 0', fontSize: '12px' }}>Tel: {laboratoryInfo.phone}</p>
                                                <p style={{ margin: '2px 0', fontSize: '12px' }}>{laboratoryInfo.email}</p>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <h1 style={{ margin: '0 0 8px 0', color: '#1e40af', fontSize: '24px' }}>DEVIS</h1>
                                                <p style={{ margin: '2px 0', fontWeight: 'bold', fontSize: '14px' }}>N° {caseNumStr}</p>
                                                <p style={{ margin: '2px 0', fontSize: '13px' }}>Date: {new Date().toLocaleDateString('fr-FR')}</p>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'flex-end' }}>
                                            <div style={{ width: '55%', border: '1px solid #e2e8f0', padding: '14px', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                                                <h3 style={{ marginTop: '0', fontSize: '12px', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>À l'attention de:</h3>
                                                <p style={{ fontWeight: 'bold', margin: '2px 0', fontSize: '14px', color: '#1e293b' }}>Dr. {order.practitionerName || '—'}</p>
                                                {practitionerClient?.address && <p style={{ margin: '2px 0', fontSize: '12px', color: '#64748b' }}>{practitionerClient.address}</p>}
                                                <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Patient :</strong> {order.patientName || '—'}</p>
                                                {(order.patientReference || order.patient_reference) && (
                                                    <p style={{ margin: '2px 0', fontSize: '13px' }}>Référence : <strong>{order.patientReference || order.patient_reference}</strong></p>
                                                )}
                                            </div>
                                        </div>

                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '25px' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#1e40af', color: 'white' }}>
                                                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px' }}>Désignation des travaux</th>
                                                    <th style={{ textAlign: 'center', padding: '10px 4px', width: '120px', fontSize: '12px' }}>Dent</th>
                                                    <th style={{ textAlign: 'center', padding: '10px', width: '70px', fontSize: '12px' }}>Quantité</th>
                                                    <th style={{ textAlign: 'right', padding: '10px', width: '85px', fontSize: '12px' }}>P.U. HT</th>
                                                    <th style={{ textAlign: 'right', padding: '10px', width: '95px', fontSize: '12px' }}>Montant HT</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((item, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                        <td style={{ padding: '10px', fontSize: '13px' }}>{item.description}</td>
                                                        <td style={{ textAlign: 'center', padding: '10px 4px', fontSize: '12.5px', fontWeight: 'bold' }}>{item.toothId}</td>
                                                        <td style={{ textAlign: 'center', padding: '10px', fontSize: '13px' }}>{item.quantity}</td>
                                                        <td style={{ textAlign: 'right', padding: '10px', fontSize: '13px' }}>{Number(item.unitPrice).toFixed(2)} €</td>
                                                        <td style={{ textAlign: 'right', padding: '10px', fontSize: '13px', fontWeight: 'bold' }}>{Number(item.price).toFixed(2)} €</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
                                            <div style={{ width: '280px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid #1e40af', fontSize: '18px', fontWeight: 'bold', color: '#1e40af' }}>
                                                    <span>TOTAL NET:</span>
                                                    <span>{total.toFixed(2)} €</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '25px', marginTop: '25px', paddingTop: '15px', borderTop: '2px solid #e2e8f0', fontSize: '11px' }}>
                                            <div>
                                                <h4 style={{ margin: '0 0 6px 0', color: '#1e40af', fontSize: '12px' }}>MENTIONS DMSM & TRAÇABILITÉ</h4>
                                                <p style={{ margin: '2px 0', fontSize: '10.5px', color: '#475569', lineHeight: '1.4' }}>
                                                    Ce devis correspond à la fabrication d'un Dispositif Médical Sur Mesure (DMSM) conforme à la réglementation européenne RDM (UE) 2017/745.
                                                </p>
                                            </div>
                                            <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                                                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#475569' }}>Bon pour accord & Signature</span>
                                                <div style={{ height: '40px' }}></div>
                                            </div>
                                        </div>

                                        <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '9px', color: '#94a3b8', borderTop: '1px solid #f1f5f9', paddingTop: '8px', lineHeight: '1.4' }}>
                                            <p style={{ margin: '2px 0', fontWeight: 'bold' }}>{laboratoryInfo.fullName} - SIRET: {laboratoryInfo.siret} - TVA: {laboratoryInfo.tva}</p>
                                            <p style={{ margin: '2px 0' }}>Devis valable 3 mois à compter de sa date d'émission. Paiement à 30 jours fin de mois.</p>
                                        </div>
                                    </div>
                                );
                            }

                            // ================= 4. FACTURE =================
                            if (activeDocType === 'INVOICE') {
                                return (
                                    <div key={`inv-${activeDocType}-${order.id || index}`} id={elementId} className="bulk-print-page mb-4 shadow-sm mx-auto" style={{ backgroundColor: 'white', padding: '20mm', color: 'black', width: '210mm', minHeight: '297mm', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', borderRadius: '8px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', borderBottom: '3px solid #1e3a8a', paddingBottom: '18px' }}>
                                            <div>
                                                <h2 style={{ margin: '0 0 8px 0', color: '#1e3a8a', fontSize: '20px' }}>{laboratoryInfo.name}</h2>
                                                <p style={{ margin: '2px 0', fontSize: '12px' }}>{laboratoryInfo.address}</p>
                                                <p style={{ margin: '2px 0', fontSize: '12px' }}>Tel: {laboratoryInfo.phone}</p>
                                                <p style={{ margin: '2px 0', fontSize: '12px' }}>{laboratoryInfo.email}</p>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <h1 style={{ margin: '0 0 8px 0', color: '#1e3a8a', fontSize: '24px' }}>FACTURE</h1>
                                                <p style={{ margin: '2px 0', fontWeight: 'bold', fontSize: '14px' }}>N° FACTURE: {caseNumStr}</p>
                                                <p style={{ margin: '2px 0', fontSize: '13px' }}>Date: {new Date().toLocaleDateString('fr-FR')}</p>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'flex-end' }}>
                                            <div style={{ width: '55%', border: '1px solid #cbd5e1', padding: '14px', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                                                <h3 style={{ marginTop: '0', fontSize: '12px', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>Facturé à:</h3>
                                                <p style={{ fontWeight: 'bold', margin: '2px 0', fontSize: '14px', color: '#1e293b' }}>Dr. {order.practitionerName || '—'}</p>
                                                {practitionerClient?.address && <p style={{ margin: '2px 0', fontSize: '12px', color: '#64748b' }}>{practitionerClient.address}</p>}
                                                <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Patient :</strong> {order.patientName || '—'}</p>
                                                {(order.patientReference || order.patient_reference) && (
                                                    <p style={{ margin: '2px 0', fontSize: '13px' }}>Référence : <strong>{order.patientReference || order.patient_reference}</strong></p>
                                                )}
                                            </div>
                                        </div>

                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '25px' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#1e3a8a', color: 'white' }}>
                                                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px' }}>Désignation des travaux</th>
                                                    <th style={{ textAlign: 'center', padding: '10px 4px', width: '120px', fontSize: '12px' }}>Dent</th>
                                                    <th style={{ textAlign: 'center', padding: '10px', width: '70px', fontSize: '12px' }}>Quantité</th>
                                                    <th style={{ textAlign: 'right', padding: '10px', width: '85px', fontSize: '12px' }}>P.U. HT</th>
                                                    <th style={{ textAlign: 'right', padding: '10px', width: '95px', fontSize: '12px' }}>Montant HT</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((item, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                        <td style={{ padding: '10px', fontSize: '13px' }}>{item.description}</td>
                                                        <td style={{ textAlign: 'center', padding: '10px 4px', fontSize: '12.5px', fontWeight: 'bold' }}>{item.toothId}</td>
                                                        <td style={{ textAlign: 'center', padding: '10px', fontSize: '13px' }}>{item.quantity}</td>
                                                        <td style={{ textAlign: 'right', padding: '10px', fontSize: '13px' }}>{Number(item.unitPrice).toFixed(2)} €</td>
                                                        <td style={{ textAlign: 'right', padding: '10px', fontSize: '13px', fontWeight: 'bold' }}>{Number(item.price).toFixed(2)} €</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
                                            <div style={{ width: '280px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '2px solid #cbd5e1', fontSize: '13px' }}>
                                                    <span>Total HT:</span>
                                                    <span>{total.toFixed(2)} €</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '2px solid #1e3a8a', fontSize: '18px', fontWeight: 'bold', color: '#1e3a8a' }}>
                                                    <span>NET À PAYER:</span>
                                                    <span>{total.toFixed(2)} €</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '25px', marginTop: '25px', paddingTop: '15px', borderTop: '2px solid #e2e8f0', fontSize: '11px' }}>
                                            <div>
                                                <h4 style={{ margin: '0 0 6px 0', color: '#1e3a8a', fontSize: '12px' }}>DÉCLARATION DE CONFORMITÉ & TRAÇABILITÉ</h4>
                                                <p style={{ margin: '2px 0', fontSize: '10.5px', color: '#475569', lineHeight: '1.4' }}>
                                                    <strong>Dispositif Médical Sur Mesure (DMSM)</strong> conforme au Règlement Européen (UE) 2017/745.
                                                </p>
                                                <div style={{ marginTop: '8px', backgroundColor: '#f8fafc', padding: '8px', borderRadius: '6px', border: '1px dashed #cbd5e1' }}>
                                                    <span style={{ fontSize: '10px', color: '#64748b' }}><strong>Traçabilité des composants / N° de Lots :</strong></span>
                                                    <div style={{ fontSize: '10px', color: '#334155', marginTop: '3px', lineHeight: '1.7' }}>
                                                        {(() => {
                                                            const detectedLots = [];
                                                            const seenLotLabels = new Set();
                                                            items.forEach(item => {
                                                                const lots = getLotNumbers(item.category, item.name || item.description, item.details?.teinte, item.notes);
                                                                lots.forEach(l => {
                                                                    if (!seenLotLabels.has(l.label)) {
                                                                        seenLotLabels.add(l.label);
                                                                        detectedLots.push(l);
                                                                    }
                                                                });
                                                            });
                                                            if (detectedLots.length > 0) {
                                                                return detectedLots.map((lot, idx) => (
                                                                    <div key={idx}><strong>{lot.label} :</strong> {lot.value}</div>
                                                                ));
                                                            }
                                                            return 'Matériaux : Lot ____________________';
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '90px' }}>
                                                <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#475569' }}>Visa & Contrôle Qualité Labo</span>
                                                <div style={{ textAlign: 'center', margin: '4px 0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <img src="/signature.png" alt="Signature" style={{ height: '45px', maxHeight: '45px', objectFit: 'contain' }} />
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '9px', color: '#94a3b8', borderTop: '1px solid #f1f5f9', paddingTop: '8px', lineHeight: '1.4' }}>
                                            <p style={{ margin: '2px 0', fontWeight: 'bold' }}>{laboratoryInfo.fullName} - SIRET: {laboratoryInfo.siret} - TVA: {laboratoryInfo.tva}</p>
                                            <p style={{ margin: '2px 0' }}>Pénalités de retard : 3 fois le taux légal. Indemnité forfaitaire de recouvrement en cas de retard : 40 €.</p>
                                        </div>
                                    </div>
                                );
                            }

                            // ================= 5. PAR DÉFAUT : BON DE LIVRAISON (BL) =================
                            return (
                                <div key={`bl-${activeDocType}-${order.id || index}`} id={elementId} className="bulk-print-page mb-4 shadow-sm mx-auto" style={{ backgroundColor: 'white', padding: '20mm', color: 'black', width: '210mm', minHeight: '297mm', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', borderRadius: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', borderBottom: '3px solid #1e293b', paddingBottom: '18px' }}>
                                        <div>
                                            <h2 style={{ margin: '0 0 8px 0', color: '#1e293b', fontSize: '20px' }}>{laboratoryInfo.name}</h2>
                                            <p style={{ margin: '2px 0', fontSize: '12px' }}>{laboratoryInfo.address}</p>
                                            <p style={{ margin: '2px 0', fontSize: '12px' }}>Tel: {laboratoryInfo.phone}</p>
                                            <p style={{ margin: '2px 0', fontSize: '12px' }}>{laboratoryInfo.email}</p>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <h1 style={{ margin: '0 0 8px 0', color: '#1e293b', fontSize: '24px' }}>BON DE LIVRAISON</h1>
                                            <p style={{ margin: '2px 0', fontWeight: 'bold', fontSize: '13px' }}>N° DOSSIER: {caseNumStr}</p>
                                            <p style={{ margin: '2px 0', fontSize: '13px' }}>Date de livraison: {new Date().toLocaleDateString('fr-FR')}</p>
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '25px', display: 'flex', justifyContent: 'flex-end' }}>
                                        <div style={{ width: '55%', border: '1px solid #e2e8f0', padding: '14px', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                                            <h3 style={{ marginTop: '0', fontSize: '12px', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>Livré à:</h3>
                                            <p style={{ fontWeight: 'bold', margin: '2px 0', fontSize: '14px', color: '#1e293b' }}>Dr. {order.practitionerName || '—'}</p>
                                            {practitionerClient?.address && <p style={{ margin: '2px 0', fontSize: '12px', color: '#64748b' }}>{practitionerClient.address}</p>}
                                            <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Patient :</strong> {order.patientName || '—'}</p>
                                            {(order.patientReference || order.patient_reference) && (
                                                <p style={{ margin: '2px 0', fontSize: '13px' }}>Référence : <strong>{order.patientReference || order.patient_reference}</strong></p>
                                            )}
                                            {order.gamme && (
                                                <p style={{ margin: '2px 0', fontSize: '12.5px' }}><strong>Gamme :</strong> {order.gamme.toLowerCase() === 'nova' ? 'Finition Nova' : order.gamme.toUpperCase()}</p>
                                            )}
                                        </div>
                                    </div>

                                    {Boolean((order.notes && order.notes.includes('[REDO')) || items.some(it => Number(it.quantity) === 0)) && (
                                        <div style={{ backgroundColor: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: '4px', padding: '6px 10px', margin: '10px 0 15px 0', color: '#92400e', fontWeight: 'bold', fontSize: '12px' }}>
                                            🔄 LIVRAISON TRAVAIL À REFAIRE (SOUS GARANTIE)
                                        </div>
                                    )}

                                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#1e293b', color: 'white' }}>
                                                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px' }}>Désignation des travaux</th>
                                                <th style={{ textAlign: 'center', padding: '10px 4px', width: '110px', fontSize: '12px' }}>Dent</th>
                                                <th style={{ textAlign: 'center', padding: '10px', width: '60px', fontSize: '12px' }}>Qté</th>
                                                <th style={{ textAlign: 'right', padding: '10px', width: '80px', fontSize: '12px' }}>P.U. HT</th>
                                                <th style={{ textAlign: 'right', padding: '10px 12px', width: '90px', fontSize: '12px' }}>Montant HT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((item, i) => {
                                                const isZeroQty = Number(item.quantity) === 0;
                                                return (
                                                    <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                        <td style={{ padding: '10px 12px', fontSize: '12.5px' }}>{item.description}</td>
                                                        <td style={{ textAlign: 'center', padding: '10px 4px', fontSize: '12px', fontWeight: 'bold', lineHeight: '1.4' }}>{item.toothId}</td>
                                                        <td style={{ textAlign: 'center', padding: '10px', fontSize: '12.5px' }}>
                                                            {isZeroQty ? (
                                                                <span style={{ display: 'inline-block', backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b', borderRadius: '4px', padding: '2px 5px', fontSize: '10px', whiteSpace: 'nowrap' }}>
                                                                    0 (Garantie)
                                                                </span>
                                                            ) : (
                                                                item.quantity
                                                            )}
                                                        </td>
                                                        <td style={{ textAlign: 'right', padding: '10px', fontSize: '12.5px', color: '#334155' }}>
                                                            {item.unitPrice !== undefined ? `${Number(item.unitPrice).toFixed(2)} €` : '—'}
                                                        </td>
                                                        <td style={{ textAlign: 'right', padding: '10px 12px', fontSize: '12.5px', fontWeight: 'bold', color: '#1e293b' }}>
                                                            {isZeroQty ? '0.00 €' : `${Number(item.price).toFixed(2)} €`}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    {/* Bloc Récapitulatif Total Prix */}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                                        <div style={{ width: '260px', backgroundColor: '#f8fafc', padding: '10px 14px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '12px' }}>
                                                <span style={{ color: '#64748b' }}>Total HT :</span>
                                                <span style={{ fontWeight: 600, color: '#1e293b' }}>{total.toFixed(2)} €</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0 0 0', borderTop: '2px solid #1e293b', marginTop: '4px', fontSize: '14.5px', fontWeight: 'bold', color: '#1e293b' }}>
                                                <span>TOTAL LIVRAISON :</span>
                                                <span>{total.toFixed(2)} €</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notes & Remarques Section */}
                                    {order.notes && order.notes.trim() !== '' && (
                                        <div style={{ marginBottom: '20px', backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                            <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#1e293b', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>NOTES & REMARQUES</h4>
                                            <div style={{ fontSize: '13px', color: '#334155', whiteSpace: 'pre-wrap' }}>
                                                <strong>Remarque générale :</strong> {order.notes}
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ marginTop: '15px', backgroundColor: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px dashed #cbd5e1' }}>
                                        <span style={{ fontSize: '10.5px', color: '#475569' }}><strong>Traçabilité des composants & Normes ISO / UE :</strong></span>
                                        <div style={{ fontSize: '10px', color: '#334155', marginTop: '3px', lineHeight: '1.7' }}>
                                            {(() => {
                                                const detectedLots = [];
                                                const seenLotLabels = new Set();
                                                items.forEach(item => {
                                                    const lots = getLotNumbers(item.category, item.description, item.details?.teinte, item.notes);
                                                    lots.forEach(l => {
                                                        if (!seenLotLabels.has(l.label)) {
                                                            seenLotLabels.add(l.label);
                                                            detectedLots.push(l);
                                                        }
                                                    });
                                                });
                                                if (detectedLots.length > 0) {
                                                    return detectedLots.map((lot, idx) => (
                                                        <div key={idx}><strong>{lot.label} :</strong> {lot.value}</div>
                                                    ));
                                                }
                                                return 'Matériaux : Lot ____________________';
                                            })()}
                                        </div>
                                    </div>

                                    {/* Déclaration d'engagement et de conformité DM sur mesure */}
                                    <div style={{ marginTop: '12px', border: '1px solid #cbd5e1', padding: '8px 12px', borderRadius: '6px', backgroundColor: '#ffffff' }}>
                                        <div style={{ fontSize: '10.5px', fontWeight: 'bold', color: '#1e293b', marginBottom: '2px', textTransform: 'uppercase' }}>
                                            Déclaration de Conformité — Dispositif Médical sur Mesure (DMSM)
                                        </div>
                                        <p style={{ margin: '0', fontSize: '9.5px', color: '#475569', lineHeight: '1.35', textAlign: 'justify' }}>
                                            {(() => {
                                                const detectedLots = [];
                                                const seenLotLabels = new Set();
                                                items.forEach(item => {
                                                    const lots = getLotNumbers(item.category, item.description, item.details?.teinte, item.notes);
                                                    lots.forEach(l => {
                                                        if (!seenLotLabels.has(l.label)) {
                                                            seenLotLabels.add(l.label);
                                                            detectedLots.push(l);
                                                        }
                                                    });
                                                });
                                                return `Le laboratoire ${laboratoryInfo.name} certifie que ce dispositif médical sur mesure a été fabriqué exclusivement pour le patient susnommé conformément à la prescription médicale du praticien et répond aux exigences générales de sécurité et de performances du Règlement Européen (UE) 2017/745 (MDR) et ${getApplicableStandardsText(detectedLots)}`;
                                            })()}
                                        </p>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px', paddingTop: '12px', borderTop: '2px solid #e2e8f0', fontSize: '11px' }}>
                                        <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '85px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#1e293b' }}>Visa Laboratoire</span>
                                            <div style={{ textAlign: 'center', margin: '4px 0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <img src="/signature.png" alt="Signature" style={{ height: '40px', maxHeight: '40px', objectFit: 'contain' }} />
                                            </div>
                                        </div>
                                        <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '85px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#1e293b' }}>Signature de Réception</span>
                                            <div style={{ fontSize: '9.5px', color: '#64748b', marginTop: 'auto', paddingTop: '4px', borderTop: '1px dashed #cbd5e1' }}>
                                                Nom, Date & Signature du praticien
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ marginTop: '15px', textAlign: 'center', fontSize: '9px', color: '#94a3b8', borderTop: '1px solid #f1f5f9', paddingTop: '6px', lineHeight: '1.4' }}>
                                        <p style={{ margin: '2px 0', fontWeight: 'bold' }}>{laboratoryInfo.fullName} - SIRET: {laboratoryInfo.siret} - TVA: {laboratoryInfo.tva}</p>
                                        <p style={{ margin: '2px 0' }}>Système de management de la qualité conforme aux exigences de la norme ISO 13485 (Dispositifs Médicaux)</p>
                                        <p style={{ margin: '2px 0' }}>Pénalités de retard : 3 fois le taux légal. Indemnité forfaitaire de recouvrement en cas de retard : 40 €.</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkDocumentModal;
